"use client";

import { Button, ButtonVariant } from "@/components/frontend/ui/button";
import { useUrl } from "@/contexts/frontend/url";
import { RenderFilters, RenderFiltersProps, SelectedFilters } from "@/lib/blocks/render-filters";
import { buildFilterQuery } from "@/lib/marketsuite/filters";
import { useFilterData } from "@/lib/marketsuite/filters/use-filter-data";
import { cn } from "@/lib/utils";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";
import { HeroCoverFormCard } from "@/payload-types";
import { useForm } from "@tanstack/react-form-nextjs";
import { useMemo, useState } from "react";

interface PropertySearchFormClientProps extends Omit<RenderFiltersProps, "form" | "selectedFilters"> {
	buttonText: string;
	buttonVariant: ButtonVariant;
	buttonPosition: HeroCoverFormCard["buttonPosition"];
}

const buttonPositionMap = {
	left: "w-fit max-w-full",
	fullWidth: "w-full",
	right: "w-fit max-w-full ms-auto",
};

export const PropertySearchFormClient: React.FC<PropertySearchFormClientProps> = ({ buttonText, buttonVariant, buttonPosition, ...props }) => {
	const { userDefinedRoutes } = useUrl();
	const [filterState, setFilterState] = useState<SelectedFilters>({});
	const filterData = useFilterData(props.filterset, filterState);

	const url = useMemo(() => {
		const listingType = filterState["listing-type"];

		// This might not be the best way of handling this edgecase,
		// but the chance of it actually happening should be 0
		if (!Array.isArray(listingType)) return undefined;

		if (!listingType || listingType.length === 0) return undefined;

		const key = listingType[0];
		if (key === undefined) return undefined;

		const query = buildFilterQuery({
			selectedFilters: filterState,
			listingType: listingType[0],
		});

		return userDefinedRoutes.ms[key]?.urlBase + `?${query}`;
	}, [filterState]);

	const form = useForm();

	return (
		<form
			className={cn("ms-form-" + resolveRelationshipId(props.formVariant), "space-y-3.5")}
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<RenderFilters
				{...props}
				form={form}
				filterData={filterData ?? undefined}
				selectedFilters={filterState}
				onValueChange={(value, blockType) => setFilterState((fs) => ({ ...fs, [blockType]: value }))}
			/>

			<Button disabled={typeof url === "undefined"} className={buttonPositionMap[buttonPosition ?? "left"]} href={url} variant={buttonVariant}>
				{buttonText}
			</Button>
		</form>
	);
};
